import {
  BodyText,
  Button,
  colorHexCodes,
  ColorInput,
  Colorpicker,
  ColorpickerColorGroup,
  createModal,
  cssBase,
  IconArrowLeft,
  isSameColorString,
  ModalPopover,
  ModalPopOverParentSelector,
  SpaceBetween,
  spacing,
  styled,
  TextField,
  useModal,
} from '@lessonup/ui-components';
import { throttle } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { HexAlphaColorPicker, HexColorPicker } from 'react-colorful';
import { useBoolean } from 'usehooks-ts';
import { useFeatureFlags } from '../../../../../utils/featureFlags/useFeatureFlags';
import { useEditorTranslation } from '../../../hooks/useEditorTranslation';
import { ActivatableButton } from '../../ActivatableButton';
import { colorPickerPlaceholderColor } from './ColorpickerModal.utils';
import { EyeDropTool } from './EyeDropper';

const templateColors: string[] = [
  '#FFFFFF',
  '#001343',
  '#E5FF45',
  '#4E7FFF',
  '#D2D1FF',
  '#24243C',
  '#AE977F',
  '#EC484D',
  '#FDEDED',
  '#4CAF50',
  '#E4F3E5',
  '#E65100',
  '#FFF3E0',
  '#F4F3FF',
];

interface BaseColorpickerProps {
  selectedColor: string;
  onChange: (color: string, isCustom: boolean, hide?: boolean) => void;
  hideTransparentOption?: boolean;
  /** includes hideTransparentOption */
  disableAlpha?: boolean;
  themeColors: string[];
}

interface ColorpickerModalProps extends BaseColorpickerProps {
  parentSelector: ModalPopOverParentSelector;
  onBeforeClose: () => void;
}

interface ColorpickerModalContentProps extends BaseColorpickerProps {
  close: () => void;
}

export const ColorpickerModal = createModal(
  ({
    parentSelector,
    selectedColor,
    onChange,
    hideTransparentOption,
    onBeforeClose,
    disableAlpha = false,
    themeColors,
  }: ColorpickerModalProps) => {
    const modal = useModal();

    const close = () => {
      onBeforeClose();
      modal.hide();
    };

    return (
      <ModalPopover
        direction="left"
        modalAnchor="center"
        parentSelector={parentSelector}
        modal={modal}
        contentLabel="temp"
        onBeforeClose={onBeforeClose}
      >
        <ColorpickerModalContent
          selectedColor={selectedColor}
          onChange={onChange}
          hideTransparentOption={hideTransparentOption || false}
          disableAlpha={disableAlpha}
          close={close}
          themeColors={themeColors}
        />
      </ModalPopover>
    );
  }
);

export const ColorpickerModalContent: React.FC<ColorpickerModalContentProps> = ({
  selectedColor,
  onChange,
  hideTransparentOption,
  disableAlpha = false,
  close,
  themeColors = templateColors,
}) => {
  const isCustomColor = (color: string) => {
    return !themeColors.concat(colorHexCodes, 'transparent').some((c) => isSameColorString(c, color));
  };
  const [page, setPage] = useState<'start' | 'custom'>('start');
  const [color, setColor] = useState<string>(selectedColor);
  const [isCustom, setIsCustom] = useState<boolean>(isCustomColor(selectedColor));
  const isHidden = useBoolean(false);

  const handleOnChange = useCallback(
    (color: string, isCustom: boolean, closeOnChange = true) => {
      setColor(color);
      onChange(color, isCustom);
      setIsCustom(isCustom);
      if (closeOnChange) {
        close?.();
      }
    },
    [onChange, close]
  );

  const handleSetPage = (page: 'start' | 'custom') => {
    setPage(page);
    if (page === 'start') {
      setIsCustom(isCustomColor(color));
    }
  };

  return (
    <StyledColorpickerModal style={{ display: isHidden.value ? 'none' : undefined }}>
      <StartPage
        handleOnChange={handleOnChange}
        color={color}
        setPage={handleSetPage}
        hideTransparentOption={hideTransparentOption || false}
        visible={page === 'start'}
        isCustom={isCustom}
        disableAlpha={disableAlpha}
        hide={isHidden.setTrue}
        show={isHidden.setFalse}
        themeColors={themeColors}
      />
      <CustomPage
        handleOnChange={handleOnChange}
        color={color}
        setPage={handleSetPage}
        hideTransparentOption={hideTransparentOption || false}
        visible={page === 'custom'}
        disableAlpha={disableAlpha}
        hide={isHidden.setTrue}
        show={isHidden.setFalse}
      />
    </StyledColorpickerModal>
  );
};

interface PageProps {
  setPage: (page: 'start' | 'custom') => void;
  handleOnChange: (color: string, isCustom: boolean, closeOnChange?: boolean) => void;
  color: string;
  hideTransparentOption: boolean;
  visible: boolean;
  disableAlpha: boolean;
  hide: () => void;
  show: () => void;
}

interface StartPageProps extends PageProps {
  isCustom: boolean;
  themeColors: string[];
}

const StartPage = ({
  handleOnChange: outerHandleOnChange,
  hideTransparentOption,
  color: color,
  setPage,
  visible,
  isCustom,
  disableAlpha,
  themeColors,
}: StartPageProps) => {
  const { t } = useEditorTranslation();

  const handleOnChange = (color: string) => {
    outerHandleOnChange(color, false);
  };

  return (
    <SpaceBetween direction="y" spacing={spacing.size16} style={{ visibility: visible ? undefined : 'hidden' }}>
      <Colorpicker onChange={handleOnChange} selectedColor={color}>
        <ColorpickerColorGroup
          colors={themeColors}
          onChange={handleOnChange}
          title={t('colorPicker.themeColors')}
          name="template"
          selectedColor={color}
        />
      </Colorpicker>
      <SpaceBetween
        direction="x"
        spacing={spacing.size8}
        justifyContent="center"
        alignItems="center"
        className={buttonBarStyle}
      >
        {!hideTransparentOption && !disableAlpha && (
          <ActivatableButton
            buttonType="neutral"
            showStroke
            buttonShape="rect"
            onClick={() => handleOnChange('transparent')}
            iconStart={<NestedColorInput color="transparent" />}
            size="small"
            active={color.toLowerCase() === 'transparent'}
          >
            {t('colorPicker.transparent')}
          </ActivatableButton>
        )}
        <ActivatableButton
          buttonType="neutral"
          showStroke
          buttonShape="rect"
          onClick={() => setPage('custom')}
          iconStart={<NestedColorInput color={color} />}
          size="small"
          active={isCustom}
        >
          {t('colorPicker.customButton')}
        </ActivatableButton>
      </SpaceBetween>
    </SpaceBetween>
  );
};

const buttonBarStyle = cssBase`
  width: 100%;
  > * {
    flex: 1;
  }
`;

const NestedColorInput = ({ color }: { color: string }) => {
  return (
    <ColorInput
      color={color}
      onClick={() => {}}
      selected={false}
      name=""
      ariaLabel=""
      className={nestedColorInputStyle}
    />
  );
};

const nestedColorInputStyle = cssBase`
  pointer-events: none;
`;

const CustomPage = ({
  handleOnChange,
  color: selectedColor,
  setPage,
  visible,
  disableAlpha,
  hide,
  show,
}: PageProps) => {
  const { t } = useEditorTranslation();
  // Having the state here again makes the picker more responsive
  const [colorString, setColorString] = useState<string>(
    selectedColor.toLowerCase() === 'transparent' ? '' : selectedColor.toUpperCase()
  );
  useEffect(() => {
    if (selectedColor.toLowerCase() !== 'transparent') {
      setColorString(selectedColor.toUpperCase());
    }
  }, [selectedColor, setColorString]);

  const throttleSet = useMemo(() => throttle(handleOnChange, 200), [handleOnChange]);
  const { isFeatureEnabled } = useFeatureFlags();

  const handleOnChangePicker = (color: string) => {
    setColorString(color.toUpperCase());
    throttleSet(color, true, false);
  };

  const handleOnChangeInput: React.FormEventHandler<HTMLInputElement> = (event) => {
    const colorHex = event.currentTarget.value;
    setColorString(colorHex);
    const regex = disableAlpha ? /^#(?:[0-9a-fA-F]{3}){1,2}$/ : /^#(?:[0-9a-fA-F]{3,4}){1,2}$/;
    if (colorHex.match(regex)) {
      handleOnChange(colorHex, true, false);
    }
  };

  return (
    <CustomPageWrapper style={{ visibility: visible ? undefined : 'hidden' }}>
      <SpaceBetween direction="y" spacing={spacing.size16} justifyContent="center">
        <SpaceBetween direction="x" spacing={spacing.size8} alignItems="center">
          <Button buttonType="neutral" onClick={() => setPage('start')} iconStart={<IconArrowLeft />} />
          <BodyText weight="bold">{t('colorPicker.customPageTitle')}</BodyText>
        </SpaceBetween>
        <HexWrapper>
          {disableAlpha ? (
            <HexColorPicker color={selectedColor} onChange={handleOnChangePicker} />
          ) : (
            <HexAlphaColorPicker color={selectedColor} onChange={handleOnChangePicker} />
          )}
        </HexWrapper>
        <TextField
          size="medium"
          value={colorString}
          type="text"
          label={disableAlpha ? t('colorPicker.hexInputLabel') : t('colorPicker.hexInputLabelAlpha')}
          onChange={handleOnChangeInput}
          placeholder={colorPickerPlaceholderColor}
          endSlot={
            isFeatureEnabled('editorEyeDropper') && (
              <EyeDropTool
                handleOnChange={handleOnChangePicker}
                selectedColor={selectedColor}
                hide={hide}
                show={show}
              />
            )
          }
        />
      </SpaceBetween>
    </CustomPageWrapper>
  );
};

const padding = spacing.size16;

const HexWrapper = styled.div`
  margin: auto;
`;

const CustomPageWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  padding: ${padding};
`;

const StyledColorpickerModal = styled.div`
  width: 352px;
  padding: ${padding};
  overflow: hidden;
`;
